import { Camera, Palette } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import type { TalkingAvatarStyle } from "@/pages/TalkingAvatar/types";

interface TalkingAvatarStylePickerProps {
  /** Display number — shifts with the chosen image mode path. */
  stepNumber: number;
  value: TalkingAvatarStyle | null;
  onChange: (next: TalkingAvatarStyle) => void;
}

export function TalkingAvatarStylePicker({ stepNumber, value, onChange }: TalkingAvatarStylePickerProps) {
  const { t } = useLanguage();
  return (
    <div className="space-y-4 md:space-y-5">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.talkingAvatar.stepPrefix.replace("{n}", String(stepNumber))}
          {t.talkingAvatar.stylePickerTitle}
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
          {t.talkingAvatar.stylePickerHint}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <Card
          icon={Camera}
          title={t.talkingAvatar.styleRealisticTitle}
          subtitle={t.talkingAvatar.styleRealisticSubtitle}
          isActive={value === "realistic"}
          onClick={() => onChange("realistic")}
        />
        <Card
          icon={Palette}
          title={t.talkingAvatar.styleCartoonTitle}
          subtitle={t.talkingAvatar.styleCartoonSubtitle}
          isActive={value === "cartoon"}
          onClick={() => onChange("cartoon")}
        />
      </div>
    </div>
  );
}

function Card({
  icon: Icon,
  title,
  subtitle,
  isActive,
  onClick,
}: {
  icon: typeof Camera;
  title: string;
  subtitle: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        "group flex flex-col items-start gap-2 p-4 md:p-5 rounded-2xl border text-start transition-all duration-200",
        isActive
          ? "bg-gradient-to-br from-primary/15 to-primary/5 border-primary shadow-md ring-2 ring-primary/30"
          : "bg-card border-border hover:border-primary/40 hover:shadow-sm md:hover:-translate-y-0.5",
      )}
    >
      <div
        className={cn(
          "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
          isActive ? "bg-primary text-white" : "bg-primary/10 text-primary",
        )}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="space-y-0.5">
        <p className="text-sm md:text-base font-semibold text-foreground">{title}</p>
        <p className="text-xs md:text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </button>
  );
}
